/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import * as d3 from 'd3';
import type { Arc, BaseType, HierarchyRectangularNode, Selection, Transition } from 'd3';
import type { SunburstDataItem } from '../../../reducers/reporting-frameworks';

interface ArcCoords {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

interface SunburstNode extends HierarchyRectangularNode<SunburstDataItem> {
  current?: ArcCoords;
  target?: ArcCoords;
}

interface D3Objects {
  dataHierarchy: SunburstNode;
  g: Selection<SVGGElement, unknown, HTMLElement, unknown>;
  arc: Arc<unknown, ArcCoords>;
  arcVisible: (d: ArcCoords) => boolean;
  labelVisible: (d: ArcCoords) => boolean;
  labelTransform: (d: ArcCoords) => string;
  path: Selection<SVGPathElement, SunburstNode, SVGGElement, unknown>;
  label: Selection<SVGTextElement, SunburstNode, SVGGElement, unknown>;
  centerCircle: Selection<SVGCircleElement, SunburstNode, HTMLElement, unknown>;
  centerText: Selection<SVGTextElement, SunburstNode, HTMLElement, unknown>;
  handleRootChange: (p: SunburstNode | undefined) => void;
}

interface ReportingFrameworkChartProps {
  data: SunburstDataItem;
  code?: string;
  handleSetSelected: (selected: SunburstNode) => void;
}

interface ReportingFrameworkChartState {
  width: number;
  height: number;
}

// current/target are always assigned (root.each / handleRootChange) before they are read
const currentOf = (d: SunburstNode): ArcCoords => d.current ?? d;
const targetOf = (d: SunburstNode): ArcCoords => d.target ?? d;

const getColour = (sdgGoal: string) => {
  const colours = [
    '#f1002f',
    '#d99e3c',
    '#219b52',
    '#c81035',
    '#f83631',
    '#00aed9',
    '#ffb437',
    '#921036',
    '#f56a2f',
    '#e60080',
    '#ff993a',
    '#d48a37',
    '#497744',
    '#007db8',
    '#3bb056',
    '#005586',
    '#0c3866'
  ];
  return colours[Number(sdgGoal) - 1];
}

class ReportingFrameworkChart extends Component<ReportingFrameworkChartProps, ReportingFrameworkChartState> {

  state: ReportingFrameworkChartState = {
    width: 968,
    height: 968,
  };

  svg: Selection<SVGSVGElement, unknown, HTMLElement, unknown> | undefined = undefined;
  containerRef = React.createRef<HTMLDivElement>();
  d3objects: Partial<D3Objects> = {};

  updateDimensions = () => {
    const current = this.containerRef.current;
    if (!current) {
      return;
    }
    this.setState({ width: current.clientWidth, height: current.clientWidth }, () => {
      this.plotChart('graph', this.props.data);
    });
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    this.updateDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(prevProps: ReportingFrameworkChartProps) {
    const dataHasChanged = prevProps.data !== this.props.data;
    if (dataHasChanged) {
      this.plotChart('graph', this.props.data);
    }
    if (this.d3objects) {
      const { dataHierarchy, handleRootChange } = this.d3objects;
      if (prevProps.code !== this.props.code && dataHierarchy && handleRootChange) {
        const p = this.getSelected(dataHierarchy, this.props.code)
        handleRootChange(p);
      }
    }
  }

  render() {
    return <div id='graph' ref={this.containerRef} ></div>
  }

  getSelected = (dataHierarchy: SunburstNode, code?: string) => {
    if (!code) {
      return dataHierarchy;
    }

    let selectedRoot: SunburstNode | undefined = undefined;
    const recursiveFindBySDGCode = (data: SunburstNode, code?: string): boolean => {
      if (data.data.code === code) {
        selectedRoot = data;
        return true;
      }
      if (!data.children) {
        selectedRoot = undefined;
        return false;
      }

      return data.children.some((c) => {
        return recursiveFindBySDGCode(c, code);
      });
    }
    recursiveFindBySDGCode(dataHierarchy, this.props.code);

    return selectedRoot;
  }

  setSelected = (p: SunburstNode) => {
    this.props.handleSetSelected(p);
  }

  plotChart(chartElementId: string, data: SunburstDataItem) {

    const _this = this;

    const size = Math.min(this.state.width, this.state.height);
    const radius = size / 6;
    const hierarchy = d3.hierarchy(data).sum(d => d.value ?? 0);
    const func = d3.partition<SunburstDataItem>().size([2 * Math.PI, hierarchy.height + 1]);
    // partition() lays out and returns the same root node, now typed with x0/x1/y0/y1
    const root: SunburstNode = func(hierarchy);
    const dataHierarchy = root;

    root.each(d => d.current = d);

    if (this.svg) {
      this.svg.remove();
    }

    this.svg = d3.select('#' + chartElementId).append('svg')
      .style('width', '100%')
      .style('height', '100%')
      .style('font', '12px InterVariable, sans-serif');

    const g = this.svg.append('g')
      .attr('transform', `translate(${size / 2},${size / 2})`);

    const arc = d3.arc<unknown, ArcCoords>()
      .startAngle(d => d.x0)
      .endAngle(d => d.x1)
      .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius * 1.5)
      .innerRadius(d => d.y0 * radius)
      .outerRadius(d => Math.max(d.y0 * radius, d.y1 * radius - 1));

    const arcVisible = (d: ArcCoords) => {
      return d.y1 <= 3 && d.y0 >= 0 && d.x1 > d.x0;
    }

    const labelVisible = (d: ArcCoords) => {
      return d.y1 <= 3 && d.y0 >= 1 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03;
    }

    const labelTransform = (d: ArcCoords) => {
      const x = (d.x0 + d.x1) / 2 * 180 / Math.PI;
      const y = (d.y0 + d.y1) / 2 * radius;
      return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
    }

    function mouseover(this: SVGPathElement, event: MouseEvent, d: SunburstNode) {
      const opacity = arcVisible(currentOf(d)) ? 1 : 0;
      d3.select(this).attr('fill-opacity', opacity);
    }

    function mouseout(this: SVGPathElement, event: MouseEvent, d: SunburstNode) {
      const opacity = arcVisible(currentOf(d)) ? (d.children ? 0.6 : 0.4) : 0;
      d3.select(this).attr('fill-opacity', opacity);
    }

    const path = g.append('g')
      .selectAll<SVGPathElement, SunburstNode>('path')
      .data(root.descendants().slice(1))
      .join('path')
      .attr('fill', d => { while (d.depth > 1 && d.parent) d = d.parent; return getColour(d.data.code); })
      .attr('fill-opacity', d => arcVisible(currentOf(d)) ? (d.children ? 0.6 : 0.4) : 0)
      .attr('cursor', d => arcVisible(currentOf(d)) ? 'pointer' : '')
      .attr('d', d => arc(currentOf(d)))
      .on('mouseover', mouseover)
      .on('mouseout', mouseout)
      .on('click', (event, d) => _this.setSelected(d));

    path.append('title')
      .text(d => `${d.ancestors().map(d => d.data.name).reverse().join(' / ')}`);

    const label = g.append('g')
      .attr('pointer-events', 'none')
      .attr('text-anchor', 'middle')
      .style('user-select', 'none')
      .selectAll<SVGTextElement, SunburstNode>('text')
      .data(root.descendants().slice(1))
      .join('text')
      .attr('dy', '0.35em')
      .attr('fill-opacity', d => +labelVisible(currentOf(d)))
      .attr('transform', d => labelTransform(currentOf(d)))
      .text(d => d.data.name);

    function centerMouseOver() {
      centerCircle.attr('fill-opacity', 0.6);
    }
    function centerMouseOut() {
      centerCircle.attr('fill-opacity', 0)
    }

    const centerCircle = g.append('circle')
      .datum(root)
      .attr('r', radius - 2)
      .attr('fill', '#ffffff')
      .attr('fill-opacity', 0)
      .attr('pointer-events', 'all')
      .style('cursor', 'pointer')
      .on('mouseover', centerMouseOver)
      .on('mouseout', centerMouseOut)
      .on('click', (event, d) => _this.setSelected(d));

    const centerText = g.append('text')
      .datum(root)
      .attr('pointer-events', 'none')
      .attr('dy', '0.5em')
      .style('text-anchor', 'middle')
      .style('font', '32px InterVariable, sans-serif')
      .text(d => d.data.name);

    const handleRootChange = (p: SunburstNode | undefined) => {

      if (!p || !arcVisible(currentOf(p))) {
        return;
      }

      centerCircle.datum(p.parent || root);
      centerText.text(p.data.name);

      root.each(d => d.target = {
        x0: Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
        x1: Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
        y0: Math.max(0, d.y0 - p.depth),
        y1: Math.max(0, d.y1 - p.depth)
      });

      // Why: @types/d3 wants Transition<BaseType> for transition(t); a typed g.transition() isn't assignable (this-type variance)
      const t = g.transition().duration(500) as unknown as Transition<BaseType, unknown, HTMLElement, unknown>;

      // Transition the data on all arcs, even the ones that aren’t visible,
      // so that if this transition is interrupted, entering arcs will start
      // the next transition from the desired position.
      path.transition(t)
        .tween('data', d => {
          const i = d3.interpolate(currentOf(d), targetOf(d));
          return t => d.current = i(t);
        })
        .filter(function (d) {
          return Boolean(Number(this.getAttribute('fill-opacity')) || arcVisible(targetOf(d)));
        })
        .attr('fill-opacity', d => arcVisible(targetOf(d)) ? (d.children ? 0.6 : 0.4) : 0)
        .attr('cursor', d => arcVisible(targetOf(d)) ? 'pointer' : '')
        .attrTween('d', d => () => arc(currentOf(d)) ?? '');

      label.filter(function (d) {
        return Boolean(Number(this.getAttribute('fill-opacity')) || labelVisible(targetOf(d)));
      }).transition(t)
        .attr('fill-opacity', d => +labelVisible(targetOf(d)))
        .attrTween('transform', d => () => labelTransform(currentOf(d)));
    }

    _this.d3objects = {
      dataHierarchy,
      g,
      arc,
      arcVisible,
      labelVisible,
      labelTransform,
      path,
      label,
      centerCircle,
      centerText,
      handleRootChange,
    };

    if (this.props.code) {
      handleRootChange(this.getSelected(dataHierarchy, this.props.code));
    }

    return this.svg.node();
  }
}

export default ReportingFrameworkChart;
